import { useEffect, useState } from "react";
import PaymentMethods from "@/components/PaymentMethods";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Check, X, CheckCircle2, XCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SectionHead } from "@/components/SectionHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (v) => `${Math.round(v).toLocaleString("fr-FR")} FCFA`;

const EstimatePage = () => {
  const { t } = useLanguage();
  const e = t.estimate;
  const { id } = useParams();
  const [params] = useSearchParams();
  const key = params.get("key") || "";
  const action = params.get("action");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (decision, text = "") => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/quotes/${id}/estimate/decision`, { key, decision, comment: text });
      setData((d) => ({ ...d, decision: r.data.decision, decided_at: r.data.decided_at }));
      toast.success(decision === "accepted" ? e.accepted : e.refused);
    } catch {
      toast.error(e.notFound);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/quotes/${id}/estimate/public`, { params: { key } })
      .then((r) => {
        setData(r.data);
        if (!r.data.decision && (action === "accept" || action === "refuse")) decide(action === "accept" ? "accepted" : "refused");
      })
      .catch(() => setError(true));
  }, [id, key]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main data-testid="estimate-page">
      <section className="relative overflow-hidden pb-24 pt-40">
        <div className="pointer-events-none absolute -right-40 top-10 h-96 w-96 rounded-full bg-cream blur-3xl" />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
          {error && <p className="rounded-2xl bg-cream px-6 py-5 text-base font-semibold text-gold" data-testid="estimate-not-found">{e.notFound}</p>}
          {data && (
            <>
              <SectionHead as="h1" overline={e.overline} title={`${e.hello} ${data.name},`} accent={`${e.title} ${data.number}`} sub={e.sub} />
              <div className="mt-10 rounded-3xl border border-brand/10 bg-white p-6 shadow-xl shadow-brand/5 sm:p-10" data-testid="estimate-summary">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">{data.service}{data.vehicle ? ` · ${data.vehicle}` : ""}</p>
                <table className="mt-6 w-full text-sm text-forest">
                  <thead>
                    <tr className="border-b border-brand/10 font-mono text-[10px] uppercase tracking-widest text-brand">
                      <th className="pb-2 text-left">Désignation</th><th className="pb-2 text-right">Qté</th><th className="pb-2 text-right">P.U.</th><th className="pb-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((it, i) => (
                      <tr key={i} className="border-b border-brand/5" data-testid={`estimate-line-${i}`}>
                        <td className="py-3">{it.description}</td><td className="py-3 text-right">{it.qty}</td><td className="py-3 text-right">{fmt(it.unit_price)}</td><td className="py-3 text-right font-semibold">{fmt(it.qty * it.unit_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-cream p-5">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-brand">{e.total}</p>
                    <p className="font-display text-3xl font-extrabold text-gold" data-testid="estimate-page-total">{fmt(data.total)}</p>
                    <p className="mt-1 text-xs text-brand">{e.validity} {data.validity_days} {e.days}</p>
                  </div>
                  <a href={`${BACKEND_URL}${data.pdf_url}`} target="_blank" rel="noopener noreferrer" data-testid="estimate-page-pdf" className="inline-flex items-center gap-2 rounded-full border-2 border-forest px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-forest transition-colors hover:bg-forest hover:text-white">
                    <FileText className="h-4 w-4" /> {e.pdf}
                  </a>
                </div>
                {data.notes && <p className="mt-5 text-sm text-brand-hover">{data.notes}</p>}

                {data.decision ? (
                  <div className={`mt-8 flex items-start gap-3 rounded-2xl p-5 ${data.decision === "accepted" ? "bg-mint" : "bg-cream"}`} data-testid={`estimate-decision-${data.decision}`}>
                    {data.decision === "accepted" ? <CheckCircle2 className="h-6 w-6 shrink-0 text-brand" /> : <XCircle className="h-6 w-6 shrink-0 text-gold" />}
                    <div className="text-sm text-forest">
                      <p className="font-semibold">{data.decision === "accepted" ? e.accepted : e.refused}</p>
                      {data.decided_at && <p className="mt-1 text-xs text-brand">{e.alreadyOn} {new Date(data.decided_at).toLocaleString("fr-FR")}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="mt-8 space-y-4">
                    <textarea rows={2} value={comment} onChange={(ev) => setComment(ev.target.value)} placeholder={e.comment} data-testid="estimate-comment-input" className="w-full resize-none rounded-xl border border-brand/20 bg-white px-4 py-3 text-sm text-forest outline-none focus:border-gold focus:ring-4 focus:ring-gold/10" />
                    <div className="flex flex-wrap gap-3">
                      <button onClick={() => decide("accepted", comment)} disabled={busy} data-testid="estimate-accept-button" className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-lg shadow-brand/25 transition-all hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-60">
                        <Check className="h-4 w-4" /> {e.accept}
                      </button>
                      <button onClick={() => decide("refused", comment)} disabled={busy} data-testid="estimate-refuse-button" className="inline-flex items-center gap-2 rounded-full border-2 border-gold px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-white disabled:opacity-60">
                        <X className="h-4 w-4" /> {e.refuse}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-6"><PaymentMethods testid="estimate-payment-methods" /></div>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default EstimatePage;
